import React, { useState, useEffect } from 'react'
import { getSessionHeaders } from '../../lib/serverSession'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''

interface CannedResponse {
  id: number
  title: string
  content: string
  category: string
  shortcut_key: string | null
  usage_count: number
  created_by: string
  is_active: boolean
  created_at: string
  updated_at: string
}

const CATEGORIES = ['general', 'billing', 'technical', 'kyc', 'affiliate', 'shareholder']

const CannedResponseManager: React.FC = () => {
  const [responses, setResponses] = useState<CannedResponse[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [showForm, setShowForm] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [filterCategory, setFilterCategory] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [formData, setFormData] = useState({ title: '', content: '', category: 'general', shortcut_key: '' })

  useEffect(() => { loadResponses() }, [])

  const loadResponses = async () => {
    setLoading(true)
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/support`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({ operation: 'load_canned_responses', data: { includeInactive: true } })
      })
      const json = await res.json()
      if (!res.ok) throw new Error(json.error)
      setResponses(json.data || [])
    } catch (err: any) { setError(err.message) }
    setLoading(false)
  }

  const handleSave = async () => {
    if (!formData.title.trim() || !formData.content.trim()) { setError('Title and content are required'); return }
    // Validate shortcut key uniqueness
    if (formData.shortcut_key.trim()) {
      const duplicate = responses.find(r =>
        r.shortcut_key === formData.shortcut_key.trim() && r.id !== editingId && r.is_active
      )
      if (duplicate) { setError(`Shortcut key "${formData.shortcut_key}" is already used by "${duplicate.title}"`); return }
    }
    setError('')
    try {
      const operation = editingId ? 'update_canned_response' : 'create_canned_response'
      const payload = editingId ? { ...formData, id: editingId } : formData
      const res = await fetch(`${API_BASE_URL}/api/admin/support`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({ operation, data: payload })
      })
      const json = await res.json()
      if (!res.ok) throw new Error(json.error)
      setShowForm(false); setEditingId(null)
      setFormData({ title: '', content: '', category: 'general', shortcut_key: '' })
      loadResponses()
    } catch (err: any) { setError(err.message) }
  }

  const handleEdit = (r: CannedResponse) => {
    setEditingId(r.id)
    setFormData({ title: r.title, content: r.content, category: r.category, shortcut_key: r.shortcut_key || '' })
    setShowForm(true)
  }

  const handleDelete = async (id: number) => {
    if (!confirm('Deactivate this canned response?')) return
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/support`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({ operation: 'delete_canned_response', data: { id } })
      })
      if (!res.ok) { const json = await res.json(); throw new Error(json.error) }
      loadResponses()
    } catch (err: any) { setError(err.message) }
  }

  const filtered = responses.filter(r => {
    if (filterCategory && r.category !== filterCategory) return false
    if (searchQuery && !r.title.toLowerCase().includes(searchQuery.toLowerCase()) && !r.content.toLowerCase().includes(searchQuery.toLowerCase())) return false
    return true
  })

  const categoryColor = (cat: string) => {
    const colors: Record<string, string> = { general: 'bg-gray-100 text-gray-700', billing: 'bg-green-100 text-green-700', technical: 'bg-blue-100 text-blue-700', kyc: 'bg-purple-100 text-purple-700', affiliate: 'bg-orange-100 text-orange-700', shareholder: 'bg-yellow-100 text-yellow-700' }
    return colors[cat] || colors.general
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Canned Responses</h3>
          <p className="text-sm text-gray-500">{responses.filter(r => r.is_active).length} active templates</p>
        </div>
        <button onClick={() => { setShowForm(true); setEditingId(null); setFormData({ title: '', content: '', category: 'general', shortcut_key: '' }) }}
          className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 text-sm font-medium">
          + New Response
        </button>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}
        <button onClick={() => setError('')} className="ml-2 font-bold">×</button></div>}

      {/* Form Modal */}
      {showForm && (
        <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
          <h4 className="text-md font-medium text-gray-900 mb-4">{editingId ? 'Edit' : 'Create'} Canned Response</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
              <input type="text" value={formData.title} onChange={e => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-yellow-500 focus:border-yellow-500"
                placeholder="e.g. Welcome / First Response" />
            </div>
            <div className="flex gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select value={formData.category} onChange={e => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-yellow-500 focus:border-yellow-500">
                  {CATEGORIES.map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
                </select>
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">Shortcut Key</label>
                <input type="text" value={formData.shortcut_key} onChange={e => setFormData({ ...formData, shortcut_key: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-yellow-500 focus:border-yellow-500"
                  placeholder="e.g. /welcome" />
              </div>
            </div>
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Content *</label>
            <textarea value={formData.content} onChange={e => setFormData({ ...formData, content: e.target.value })}
              rows={6} maxLength={5000} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-yellow-500 focus:border-yellow-500"
              placeholder="Type the response template content..." />
            <p className="text-sm text-white mt-1">{formData.content.length}/5000</p>
          </div>
          <div className="flex gap-2 justify-end">
            <button onClick={() => { setShowForm(false); setEditingId(null) }}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm">Cancel</button>
            <button onClick={handleSave}
              className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 text-sm font-medium">
              {editingId ? 'Update' : 'Create'} Response
            </button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-3 items-center">
        <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="Search responses..." />
        <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
          <option value="">All Categories</option>
          {CATEGORIES.map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
        </select>
      </div>

      {/* Response List */}
      {loading ? (
        <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-600"></div></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-8 text-gray-500">No canned responses found</div>
      ) : (
        <div className="space-y-3">
          {filtered.map(r => (
            <div key={r.id} className={`bg-white border rounded-lg p-4 ${!r.is_active ? 'opacity-50' : ''}`}>
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <h4 className="font-medium text-gray-900">{r.title}</h4>
                  <span className={`px-2 py-0.5 rounded-full text-sm font-medium ${categoryColor(r.category)}`}>{r.category}</span>
                  {r.shortcut_key && <code className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-sm">{r.shortcut_key}</code>}
                  {!r.is_active && <span className="px-2 py-0.5 bg-red-100 text-red-600 rounded-full text-sm">Inactive</span>}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-white">Used {r.usage_count}×</span>
                  <button onClick={() => handleEdit(r)} className="text-blue-600 hover:text-blue-800 text-sm">Edit</button>
                  {r.is_active && <button onClick={() => handleDelete(r.id)} className="text-red-600 hover:text-red-800 text-sm">Delete</button>}
                </div>
              </div>
              <p className="text-sm text-gray-600 whitespace-pre-line line-clamp-3">{r.content}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default CannedResponseManager
